import React, {useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import axios from "axios";

/**
 * Browser version of the "clean" PDF of an admission document.
 *
 * A browser PDF viewer cannot be scrolled to a given row, so the clinical flags modal shows this HTML instead:
 * every flagged answer carries data-flag-ids (a table row in the default layout, a box over the page image in
 * the overlay layouts), and `focusFlag` scrolls to the answer of a clicked alert.
 * The PDF itself stays one click away for printing / downloading.
 *
 * The HTML goes into a sandboxed iframe (no scripts; same origin only so the rows can be reached from here)
 * to keep the document styles away from the manager styles.
 *
 * @param focusFlag       {id} of the alert to jump to - a new object on every click, so the same alert can be re-focused
 * @param onFlagIdsLoaded receives the ids of the flags that have a row in the document, i.e. the alerts that can be jumped to
 */
const AdmissionDocumentCleanHtml = ({documentId, focusFlag, onFlagIdsLoaded, width = '100%', height = 620}) => {
    const idToken = useSelector(state => state.auth.idToken);
    const [html, setHtml] = useState(null);
    const [error, setError] = useState(false);
    const frameRef = useRef(null);

    useEffect(() => {
        setHtml(null);
        setError(false);
        onFlagIdsLoaded && onFlagIdsLoaded([]);
        axios.get(`/admission/document/${documentId}/clean-html`, {headers: {'Authorization': idToken}})
            .then(response => setHtml(response.data.html))
            .catch(() => setError(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documentId, idToken]);

    const handleLoad = () => {
        const doc = frameRef.current && frameRef.current.contentDocument;
        if (!doc || !onFlagIdsLoaded) {
            return;
        }
        const ids = new Set();
        doc.querySelectorAll('[data-flag-ids]').forEach(element =>
            element.getAttribute('data-flag-ids').split(' ').forEach(id => ids.add(Number(id)))
        );
        onFlagIdsLoaded([...ids]);
    }

    useEffect(() => {
        const frame = frameRef.current;
        const doc = frame && frame.contentDocument;
        const row = focusFlag && doc && doc.querySelector(`[data-flag-ids~="${focusFlag.id}"]`);
        if (!row) {
            return;
        }
        // scroll the frame only - row.scrollIntoView() would also scroll the modal and the page around it
        const win = frame.contentWindow;
        const rect = row.getBoundingClientRect();
        win.scrollTo({top: win.scrollY + rect.top - (win.innerHeight - rect.height) / 2, behavior: 'smooth'});

        row.classList.add('flag-focus');
        const timer = setTimeout(() => row.classList.remove('flag-focus'), 2000);
        return () => {
            clearTimeout(timer);
            row.classList.remove('flag-focus');
        }
    }, [focusFlag]);

    const pdfSrc = `${process.env.REACT_APP_API_PATH}/admission/document/${documentId}/clean-pdf?token=${encodeURIComponent(idToken)}`;

    return (
        <div>
            <div style={{marginBottom: '8px'}}>
                <a className="btn btn-sm btn-secondary" href={pdfSrc} target="_blank" rel="noopener noreferrer">Open PDF</a>
            </div>
            {error && <div className="text-danger">Failed to load the document</div>}
            {!error && html === null && <div>Loading...</div>}
            {html !== null && (
                <iframe
                    ref={frameRef}
                    title="Clean document"
                    sandbox="allow-same-origin"
                    style={{border: "none"}}
                    width={width}
                    height={height}
                    srcDoc={html}
                    onLoad={handleLoad}
                />
            )}
        </div>
    )
}

export default AdmissionDocumentCleanHtml;
